import { create } from 'zustand';
import { Event } from '../types';
import {
  getEvents,
  getEvent,
  updateEvent,
  deleteEvent,
  confirmEvent,
  syncEventToGoogle,
  parseAndCreateEvent,
  getEventConflicts,
  EventConflict,
} from '../services';

interface EventsState {
  // State
  events: Event[];
  conflicts: EventConflict[];
  selectedEvent: Event | null;
  isLoading: boolean;
  error: string | null;
  lastFetchedAt: number | null;

  // Filters
  dateRange: { start: string; end: string } | null;
  statusFilter: string | undefined;

  // Actions
  fetchEvents: (start?: string, end?: string, status?: string) => Promise<void>;
  fetchEvent: (id: string) => Promise<Event>;
  fetchConflicts: () => Promise<void>;

  createEventFromText: (text: string, inputType?: string) => Promise<Event>;
  updateEventById: (id: string, data: Partial<Event>) => Promise<Event>;
  deleteEventById: (id: string) => Promise<void>;
  confirmEventById: (id: string) => Promise<Event>;
  syncToGoogle: (id: string) => Promise<void>;

  // Selection
  selectEvent: (event: Event | null) => void;

  // Optimistic updates
  optimisticUpdate: (id: string, data: Partial<Event>) => void;
  revertOptimisticUpdate: (id: string, originalEvent: Event) => void;

  // Utility
  invalidate: () => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  events: [],
  conflicts: [],
  selectedEvent: null,
  isLoading: false,
  error: null,
  lastFetchedAt: null,
  dateRange: null,
  statusFilter: undefined,
};

const STALE_AFTER = 2 * 60 * 1000; // 2 minutes

export const useEventsStore = create<EventsState>()((set, get) => ({
  ...initialState,

  // Fetch events with optional filters (SWR: skip spinner if data exists, skip fetch if fresh + same params)
  fetchEvents: async (start, end, status) => {
    const { lastFetchedAt, dateRange, statusFilter } = get();
    const sameParams = dateRange?.start === start && dateRange?.end === end && statusFilter === status;
    const hasData = lastFetchedAt !== null;
    const isFresh = lastFetchedAt !== null && Date.now() - lastFetchedAt < STALE_AFTER;

    if (sameParams && hasData && isFresh) return;
    if (!hasData || !sameParams) set({ isLoading: true, error: null });

    try {
      const { events } = await getEvents(start, end, status, true);
      set({
        events,
        isLoading: false,
        lastFetchedAt: Date.now(),
        dateRange: start && end ? { start, end } : null,
        statusFilter: status,
        error: null,
      });
    } catch (err) {
      if (!hasData || !sameParams) {
        set({
          error: err instanceof Error ? err.message : 'Failed to load events',
          isLoading: false,
        });
      }
    }
  },

  // Fetch single event
  fetchEvent: async (id) => {
    const event = await getEvent(id);
    set((state) => ({
      events: state.events.some((e) => e.id === id)
        ? state.events.map((e) => (e.id === id ? event : e))
        : [...state.events, event],
      selectedEvent: event,
    }));
    return event;
  },

  // Fetch conflicts
  fetchConflicts: async () => {
    try {
      const { conflicts } = await getEventConflicts();
      set({ conflicts });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load conflicts' });
    }
  },

  // Create event from natural language
  createEventFromText: async (text, inputType = 'auto') => {
    const { event } = await parseAndCreateEvent(text, inputType);
    set((state) => ({ events: [...state.events, event] }));
    return event;
  },

  // Update event
  updateEventById: async (id, data) => {
    const event = await updateEvent(id, data);
    set((state) => ({
      events: state.events.map((e) => (e.id === id ? event : e)),
      selectedEvent: state.selectedEvent?.id === id ? event : state.selectedEvent,
    }));
    return event;
  },

  // Delete event
  deleteEventById: async (id) => {
    await deleteEvent(id);
    set((state) => ({
      events: state.events.filter((e) => e.id !== id),
      selectedEvent: state.selectedEvent?.id === id ? null : state.selectedEvent,
    }));
  },

  // Confirm event
  confirmEventById: async (id) => {
    const event = await confirmEvent(id);
    set((state) => ({
      events: state.events.map((e) => (e.id === id ? event : e)),
      selectedEvent: state.selectedEvent?.id === id ? event : state.selectedEvent,
    }));
    return event;
  },

  // Sync to Google Calendar
  syncToGoogle: async (id) => {
    const { event } = await syncEventToGoogle(id);
    set((state) => ({
      events: state.events.map((e) => (e.id === id ? event : e)),
      selectedEvent: state.selectedEvent?.id === id ? event : state.selectedEvent,
    }));
  },

  // Selection
  selectEvent: (event) => set({ selectedEvent: event }),

  // Optimistic updates for better UX
  optimisticUpdate: (id, data) => {
    set((state) => ({
      events: state.events.map((e) =>
        e.id === id ? { ...e, ...data } : e
      ),
    }));
  },

  revertOptimisticUpdate: (id, originalEvent) => {
    set((state) => ({
      events: state.events.map((e) =>
        e.id === id ? originalEvent : e
      ),
    }));
  },

  // Utility
  invalidate: () => set({ lastFetchedAt: null }),
  clearError: () => set({ error: null }),
  reset: () => set(initialState),
}));

// Selector hooks
export const useEvents = () => useEventsStore((state) => state.events);
export const useSelectedEvent = () => useEventsStore((state) => state.selectedEvent);
export const useEventConflicts = () => useEventsStore((state) => state.conflicts);

// Get events for a specific date
export const useEventsForDate = (date: string) =>
  useEventsStore((state) =>
    state.events.filter((e) => e.start_time.startsWith(date))
  );

export const isEventsStale = () => {
  const { lastFetchedAt } = useEventsStore.getState();
  return lastFetchedAt === null || Date.now() - lastFetchedAt > STALE_AFTER;
};

export default useEventsStore;
